import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { AppTabs } from './components/AppTabs';
import { LoadingState } from './components/StateViews';
import { DashboardPage } from './pages/DashboardPage';
import { GenerationsPage } from './pages/GenerationsPage';
import { ImportPage } from './pages/ImportPage';
import { ProductsPage } from './pages/ProductsPage';
import { StudioWizardPage } from './pages/StudioWizardPage';
import { getInitDataRaw } from './lib/telegram';

const adminBotUsername = import.meta.env.VITE_ADMIN_BOT_USERNAME ?? 'fitbot_admin_bot';
const adminBotUrl = `https://t.me/${adminBotUsername}?start=app`;
const clientBotUsername = (import.meta.env.VITE_CLIENT_BOT_USERNAME ?? 'fitbot_client_bot')
  .replace(/^@/, '')
  .trim();
const clientBotUrl = `https://t.me/${clientBotUsername}`;

const CatalogAnalyzePage = lazy(() => import('./pages/CatalogAnalyzePage'));
const GenerationPage = lazy(() => import('./pages/GenerationPage'));
const ImportJobPage = lazy(() => import('./pages/ImportJobPage'));
const ClientAppPage = lazy(() => import('./pages/ClientAppPage'));
const ProductCreatePage = lazy(() => import('./pages/ProductCreatePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const OwnerPage = lazy(() => import('./pages/OwnerPage'));
const OwnerShopsPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerShopsPage })));
const OwnerB2cPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerB2cPage })));
const OwnerGenerationsPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerGenerationsPage })));
const OwnerErrorsPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerErrorsPage })));
const OwnerBillingPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerBillingPage })));
const OwnerLeadsPage = lazy(() => import('./pages/OwnerDetailPages').then((m) => ({ default: m.OwnerLeadsPage })));

type TelegramGateSurface = 'business' | 'client';

function OutsideTelegramGate({ children, surface }: { children: ReactNode; surface: TelegramGateSurface }) {
  const [hasInitData, setHasInitData] = useState(() => getInitDataRaw().length > 0);

  useEffect(() => {
    const initData = getInitDataRaw();
    setHasInitData(initData.length > 0);
  }, []);

  if (hasInitData) return <>{children}</>;

  const isClient = surface === 'client';

  return (
    <div className="outside-telegram">
      <div className="outside-telegram__panel">
        <div className="outside-telegram__eyebrow">{isClient ? 'FitBot' : 'FitBot Business'}</div>
        <h1>{isClient ? 'Откройте FitBot в Telegram' : 'Откройте FitBot Business в Telegram'}</h1>
        <p>
          {isClient
            ? 'Примерочная защищена Telegram-входом. Откройте её из клиентского бота, чтобы продолжить.'
            : 'Эта панель защищена Telegram-входом. В обычном браузере она не получает подпись пользователя, поэтому данные магазина не загружаются.'}
        </p>
        <a className="outside-telegram__button" href={isClient ? clientBotUrl : adminBotUrl}>
          {isClient ? 'Открыть FitBot' : 'Открыть FitBot Business'}
        </a>
      </div>
    </div>
  );
}

function MiniAppLayout() {
  return (
    <OutsideTelegramGate surface="business">
      <AppTabs />
      <Outlet />
    </OutsideTelegramGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route
            path="client"
            element={(
              <OutsideTelegramGate surface="client">
                <ClientAppPage />
              </OutsideTelegramGate>
            )}
          />
          <Route element={<MiniAppLayout />}>
            <Route index element={<ProductsPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="import/:jobId" element={<ImportJobPage />} />
            <Route path="generations" element={<GenerationsPage />} />
            <Route path="generations/:id" element={<GenerationPage />} />
            <Route path="studio/new" element={<StudioWizardPage />} />
            <Route path="stats" element={<DashboardPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="owner" element={<OwnerPage />} />
            <Route path="owner/shops" element={<OwnerShopsPage />} />
            <Route path="owner/b2c" element={<OwnerB2cPage />} />
            <Route path="owner/generations" element={<OwnerGenerationsPage />} />
            <Route path="owner/errors" element={<OwnerErrorsPage />} />
            <Route path="owner/billing" element={<OwnerBillingPage />} />
            <Route path="owner/leads" element={<OwnerLeadsPage />} />
            <Route path="products/new" element={<ProductCreatePage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="catalog/analyze" element={<CatalogAnalyzePage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
